/*
  Functions to extract text content from React elements.
  There are two functions which work together:

  - getTextStringFromReactElement(): Extracts and joins text content from children of a
    React element

  - extractTextFromElement(): Called from getTextStringFromReactElement(). Extracts text
  content from various types of elements, including handling nested structures

  Usage:
  import getTextStringFromReactElement from '../../lib/getTextStringFromReactElement';

  Example usage with a multiline structure used in radio buttons:
  const multiLineElement = (
    <>
      <span>
        <strong>Multiple lines</strong>
      </span>
      <span>Line 2</span>
      <span>Line 3</span>
      <span>Line 4</span>
    </>
  );

  const elementText = getTextStringFromReactElement(multiLineElement);
*/

import {
  ReactNode,
  ReactElement,
  ReactChild,
  ReactFragment,
  ReactPortal,
  isValidElement,
} from 'react';

interface ChildProps {
  children: ReactNode;
}

interface ParentProps {
  children: ReactNode | ReactElement<ChildProps>[];
}

type ExtractableElement =
  | ReactElement<ChildProps>
  | string
  | ReactChild
  | ReactFragment
  | ReactPortal;

/**
 * Extract text content from different types of elements
 *
 * @param element - The element to extract text content from
 * @returns The extracted text content
 */
const extractTextFromElement = (element: ExtractableElement): string => {
  // If the element is already a string, return it
  if (typeof element === 'string') {
    return element;
  }

  // If the element is not a valid React element, return an empty string
  if (!isValidElement(element)) {
    return '';
  }

  // Destructure children from element.props
  const { children } = element.props;

  // If there are no children, return an empty string
  if (!children) {
    return '';
  }

  // If children is an array, combine the text of each item; otherwise, extract from a single child
  return Array.isArray(children)
    ? children.map(extractTextFromElement).join('')
    : extractTextFromElement(children);
};

/**
 * Extract text content from a parent React element
 *
 * @param element - The parent element to extract text content from
 * @returns The extracted text content
 */
const getTextStringFromReactElement = (element: ReactElement<ParentProps>) => {
  // If parent is not a valid React element, return an empty string
  if (!isValidElement(element)) {
    return '';
  }

  // Get children from parent's props and treat them as an array of extractable elements
  const children = element.props.children as ExtractableElement[];

  // Map and join text content from children and trim leading/trailing whitespace
  // Currently separated by commas as only used for multiline labels for radio buttons
  // at the moment, but this can be readressed later if necessary (e.g. by passing a prop
  // with the separator if required)
  return children.map(extractTextFromElement).join(', ').trim();
};

export default getTextStringFromReactElement;
